import React, { useContext, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { Checkbox, MenuItem, Select } from "@material-ui/core";
import { StyledBlueButton } from "../../styles/BootstrapStyles";
import {
  CANTEMO_DROPDOWN_FIELDS,
  CANTEMO_DROPDOWN_OPTIONS,
  CANTEMO_TEXT_FIELDS,
  MEDIA_SEARCH,
} from "../../constants/MediaSearch";
import { hasCantemoFilters } from "../../utils/MediaRequestUtils";
import { MediaRequestContext } from "../../context/MediaRequestContext";
import "../../styles/media-search.scss";

const CANTEMO_FILTER_FIELDS = [...CANTEMO_TEXT_FIELDS, ...CANTEMO_DROPDOWN_FIELDS];
const SELECT_ALL_VALUE = "select-all";
const CANTEMO_MENU_MAX_HEIGHT = 220;

const getNextSelectedValues = (options, selectedValues, nextValue) => {
  if (nextValue.includes(SELECT_ALL_VALUE)) {
    return selectedValues.length === options.length ? [] : [...options];
  }
  return nextValue;
};

const CantemoMultiSelect = ({ field, selectedValues, onChange }) => {
  const options = CANTEMO_DROPDOWN_OPTIONS[field.key];
  const allSelected = options.length > 0 && selectedValues.length === options.length;
  const wrapRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(0);

  const syncMenuWidth = () => {
    if (wrapRef.current) {
      setMenuWidth(wrapRef.current.clientWidth);
    }
  };

  return (
    <div className="cantemo-filter-select-wrap" ref={wrapRef}>
      <Form.Control
        as={Select}
        multiple
        autoWidth={false}
        disableUnderline
        displayEmpty
        className="formInputSelect"
        value={selectedValues}
        onOpen={syncMenuWidth}
        onChange={(e) => onChange(getNextSelectedValues(options, selectedValues, e.target.value))}
        renderValue={(selected: string[]) =>
          selected.length ? selected.join(", ") : MEDIA_SEARCH.cantemoDropdownPlaceholder
        }
        MenuProps={{
          variant: "menu" as const,
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: "bottom" as const,
            horizontal: "left" as const,
          },
          transformOrigin: {
            vertical: "top" as const,
            horizontal: "left" as const,
          },
          PaperProps: {
            className: "cantemo-filter-menu-paper",
            style: {
              maxHeight: CANTEMO_MENU_MAX_HEIGHT,
              width: menuWidth || undefined,
            },
          },
          MenuListProps: {
            className: "cantemo-filter-menu-list",
            disablePadding: true,
          },
        }}
      >
        <MenuItem className="cantemo-filter-option" value={SELECT_ALL_VALUE}>
          <Checkbox checked={allSelected} className="presetFilterCheckbox cantemo-filter-checkbox" />
          {MEDIA_SEARCH.cantemoSelectAll}
        </MenuItem>
        {options.map((option) => (
          <MenuItem className="cantemo-filter-option" key={option} value={option}>
            <Checkbox
              checked={selectedValues.indexOf(option) > -1}
              className="presetFilterCheckbox cantemo-filter-checkbox"
            />
            <span className="cantemo-filter-option-label">{option}</span>
          </MenuItem>
        ))}
      </Form.Control>
    </div>
  );
};

const MediaCantemoFilters = () => {
  const {
    cantemoFilters,
    _setCantemoFilters,
    searchMediaById,
    _setSelectedOption,
    _setValidAssets,
    _setOdsEditsData,
    _setOdsSearchIdsData,
    _setIsPromoLoading,
    _setIsCantemoLoading = () => {},
    _setPromoError,
  } = useContext(MediaRequestContext);

  const clearSearchState = () => {
    _setSelectedOption("");
    _setOdsEditsData({});
    _setOdsSearchIdsData({});
    _setValidAssets({});
    _setPromoError({});
  };

  const updateFilter = (key, value) => {
    clearSearchState();
    _setCantemoFilters({
      ...cantemoFilters,
      [key]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearSearchState();
    if (!hasCantemoFilters(cantemoFilters)) {
      return;
    }
    _setIsPromoLoading(true);
    _setIsCantemoLoading(true);
    searchMediaById();
  };

  const renderField = (field) => {
    const dropdownOptions = CANTEMO_DROPDOWN_OPTIONS[field.key];
    if (dropdownOptions) {
      const selectedValues = cantemoFilters[field.key] || [];
      return (
        <Form.Group
          className="cantemo-filter-field"
          controlId={`cantemo-${field.key}`}
          key={field.key}
        >
          <Form.Label className="media-request-formLabel">{field.label}</Form.Label>
          <CantemoMultiSelect
            field={field}
            selectedValues={selectedValues}
            onChange={(value) => updateFilter(field.key, value)}
          />
        </Form.Group>
      );
    }

    return (
      <Form.Group
        className="cantemo-filter-field"
        controlId={`cantemo-${field.key}`}
        key={field.key}
      >
        <Form.Label className="media-request-formLabel">{field.label}</Form.Label>
        <Form.Control
          type="text"
          className="formInput"
          placeholder={field.placeholderKey ? MEDIA_SEARCH[field.placeholderKey] : field.label}
          autoComplete="off"
          value={cantemoFilters[field.key]}
          onChange={(e) => updateFilter(field.key, e.target.value)}
        />
      </Form.Group>
    );
  };

  return (
    <Form onSubmit={handleSubmit} className="cantemo-filters">
      <div className="cantemo-filters-grid">
        {CANTEMO_FILTER_FIELDS.map(renderField)}
      </div>
      <div className="cantemo-search-button-wrap">
        <StyledBlueButton
          type="submit"
          className="cantemo-search-button"
          disabled={!hasCantemoFilters(cantemoFilters)}
        >
          Search
        </StyledBlueButton>
      </div>
    </Form>
  );
};

export default MediaCantemoFilters;
